/**
 * 作业**验收**（通过 / 打回）的共享动作 + 共享弹窗 —— 家长端**唯一**的一份实现。
 *
 * ## 它修的是什么（2026-10-15 用户实测）
 *
 * 用户原话：
 *
 * > 昨天孩子提交了作业，但我没有检查，就没有点通过，它就过期了，过期是没问题。
 * > 问题在于，我现在检查了作业，我想点通过，**却没有按钮了**。
 *
 * 根因不是权限、也不是接口，而是**验收这套东西只做在「今天的作业」那一支里**
 * （`ParentHomeworkPage` 的查看态）。而「超时」是**跨天的派生视图**：昨天提交、
 * 今天才超时的作业项**只会出现在超时列表里** —— 于是"孩子交了、家长想验收"
 * 这件事，在唯一看得见它的那个列表里**根本做不到**。
 *
 * ## 为什么抽成这个文件，而不是在超时列表里再写一遍
 *
 * 本项目反复因为"同一规则两处实现"出问题（见 `homework-draft.ts` 的模块头注释）。
 * 验收这一套里，**四处**都必须只有一份，否则两边迟早漂移：
 *
 *   1. **打的哪个接口**：`POST /checkins/:id/approve|reject`（既有端点，未新增）；
 *   2. **权限判据**：服务端 `canApproveCheckin(actor)`（前端不自己判角色）；
 *   3. **缓存失效链**：`useHomeworkApproval()`（作业日 + **超时队列** + 今日清单 +
 *      积分 + 账本 + 统计），两个视图共用同一个 hook 就不可能"只刷一个"；
 *   4. **文案**：`@fht/shared` 的 `describeHomeworkApprovalResult()` /
 *      `describeHomeworkRejectResult()` —— 分值为 0 时"没有加分"这句必须一致。
 *
 * 组件侧同样只有一份：`ApprovalActions`（`components/homework/ApprovalActions.tsx`）
 * 被「今天的作业」的作业项与「超时」行**同时复用**。
 *
 * ## 幂等（与既有实现逐字一致）
 *
 * - **通过**幂等：服务端同一幂等键不会重复加分，所以不需要二次确认；
 * - **打回不幂等**：第二次会被后端以 409 拒绝，而 `useHomeworkApproval` 的
 *   `onError` 也会失效缓存 —— 界面因此立刻回到真实状态，不会停在旧的"待验收"。
 */
import { useState, type ReactElement } from 'react';
import {
  HomeworkApprovalRejectDialog,
  type HomeworkRejectTarget,
} from '../components/homework/HomeworkApprovalRejectDialog';
import { useHomeworkApproval } from './domainQueries';
import { useToast } from '../components/ui/Toast';
import { newIdempotencyKey } from './api/client';
import { toErrorMessage } from './api/error';
import {
  describeHomeworkApprovalResult,
  describeHomeworkRejectResult,
} from '@fht/shared';

/**
 * 点「通过 / 打回」时**必须**带上的三个字段（两个视图的数据来源不同，但都满足它）。
 *
 * - `checkinId`：真正常识别的就是**打卡记录**（不是 habitId）。
 *   「今天的作业」里它是 `item.checkin.checkinId`，「超时」队列里它是条目自己的 `id`
 *   （`HomeworkOverdueEntry.id` 的注释写明了这一点）。
 * - `name`：Toast 里要说"通过了哪一项"，否则家长连着点几条后不知道发生了什么。
 * - `pointsPerUnit`：决定 Toast 说"加了 N 分"还是"这一项不计分，没有加分"。
 *   拿不到就传 `null`（**不要吞成 0**，那会把"不知道"说成"不计分"）。
 */
export interface HomeworkApprovalActionTarget {
  checkinId: string;
  name: string;
  pointsPerUnit: number | null;
  /**
   * 这一项属于哪一天（可选）。
   *
   * 两个视图都传（「今天的作业」传当前查看的日期；「超时」传 `entry.date`），
   * 于是打回弹窗里**总有**"哪一天"这一行 —— 一条跨天的欠账只写名字的话，
   * 家长看不出它是昨天还是上周的。
   */
  date?: string;
}

/** 打回弹窗里要显示的上下文（名称 + 属于哪一天，超时列表的项必须看得出"出处"）。 */
export interface HomeworkApprovalRejectRequest extends HomeworkRejectTarget {
  checkinId: string;
  pointsPerUnit: number | null;
}

export interface HomeworkApprovalActions {
  onApprove: (target: HomeworkApprovalActionTarget) => void;
  onReject: (target: HomeworkApprovalActionTarget) => void;
  /** 通过 / 打回有一个在飞（按钮据此禁用，避免连点）。 */
  busy: boolean;
  /** 打回弹窗里那条错误（仅"打回"会用到）。 */
  rejectError: string | null;
  /** 打回弹窗（**唯一一份**）—— 页面在根上渲染一次即可。 */
  rejectDialog: ReactElement;
}

export function useHomeworkApprovalActions(studentId: string): HomeworkApprovalActions {
  const toast = useToast();
  /**
   * 写操作与**缓存失效链**：直接复用既有 hook（⛔ 不在这里另写一遍失效，
   * 那正是"只刷一个、另一个还是旧数"的来源）。它顺带覆盖超时队列
   * —— `['students', id, 'homework']` 是整个前缀，含 `.../overdue`。
   */
  const approval = useHomeworkApproval(studentId);
  const rejectMutation = approval.reject;
  const approveMutation = approval.approve;

  const [rejectTarget, setRejectTarget] = useState<HomeworkApprovalRejectRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState<string | null>(null);
  /**
   * 打回的幂等键：**一次用户意图一个键**，在打开弹窗那一刻生成。
   *
   * ⚠️ 不要在 `confirmReject` 里每次新生成一个：`newIdempotencyKey()` 的注释写得很清楚
   * ——"弱网重试必须复用同一个键"，每次重试换键等于后端的幂等保护彻底失效
   * （会留下第二条打回记录）。也刻意**不在渲染里**生成：渲染可能被调用多次
   * （严格模式 / 并发渲染），那样同一次点击每次重渲染都会换键。
   */
  const [rejectKey, setRejectKey] = useState('');

  const onApprove = (target: HomeworkApprovalActionTarget) => {
    if (!target.checkinId) {
      toast.showToast('这一项还没有可验收的提交记录，刷新一下再试', 'error');
      return;
    }
    // void：调用方不需要等；错误在下面统一 Toast（与既有实现一致）
    void (async () => {
      try {
        const result = await approveMutation.mutateAsync({
          checkinId: target.checkinId,
          idempotencyKey: newIdempotencyKey(`approve:${target.checkinId}`),
        });
        /**
         * ⚠️ "通过但没加分"必须**说出来**（文案在 shared，与超时列表逐字一致）：
         * 服务端只在 `pointsPerUnit > 0` 时入账，分值为 0 的项通过后余额一动不动。
         * 只回一句"已通过"会让家长以为"孩子的分怎么没变"是系统坏了。
         */
        toast.showToast(
          describeHomeworkApprovalResult(
            target.name,
            target.pointsPerUnit,
            result.pointsAwarded,
          ),
          target.pointsPerUnit !== null && target.pointsPerUnit <= 0 ? 'info' : 'success',
        );
      } catch (err) {
        toast.showToast(toErrorMessage(err), 'error');
      }
    })();
  };

  const onReject = (target: HomeworkApprovalActionTarget) => {
    setRejectTarget({
      checkinId: target.checkinId,
      name: target.name,
      pointsPerUnit: target.pointsPerUnit,
      ...(target.date ? { date: target.date } : {}),
    });
    setRejectReason('');
    setRejectError(null);
    setRejectKey(newIdempotencyKey('reject'));
  };

  const closeReject = () => {
    setRejectTarget(null);
    setRejectReason('');
    setRejectError(null);
    setRejectKey('');
  };

  const confirmReject = () => {
    const target = rejectTarget;
    if (!target) return;
    if (!target.checkinId) {
      toast.showToast('这一项还没有可验收的提交记录，刷新一下再试', 'error');
      closeReject();
      return;
    }
    setRejectError(null);
    void (async () => {
      try {
        await rejectMutation.mutateAsync({
          checkinId: target.checkinId,
          reason: rejectReason.trim(),
          // 复用打开弹窗时那一个键（见 `rejectKey` 的注释）
          idempotencyKey: rejectKey || newIdempotencyKey('reject'),
        });
        toast.showToast(describeHomeworkRejectResult(target.name, rejectReason), 'info');
        closeReject();
      } catch (err) {
        const message = toErrorMessage(err);
        setRejectError(message);
        toast.showToast(message, 'error');
        /**
         * 失败也关掉弹窗：`useHomeworkApproval` 的 `onError` 已经失效过缓存，
         * 界面会回到真实状态（例如这一项其实已经被别处验收过了 → 409）。
         * 停在弹窗里只会让人以为"再点一次就好了"。
         */
        setRejectTarget(null);
      }
    })();
  };

  return {
    onApprove,
    onReject,
    busy: approveMutation.isPending || rejectMutation.isPending,
    rejectError,
    rejectDialog: (
      <HomeworkApprovalRejectDialog
        target={rejectTarget}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        error={rejectError}
        pending={rejectMutation.isPending}
        onClose={closeReject}
        onConfirm={confirmReject}
      />
    ),
  };
}
